import * as React from 'react';
import { History, Location } from 'history';
import { object as PropObject } from 'prop-types';

import { humanize, Organization, User, ValidationError } from '../gs-core/common';

import {
  Doc, DocModel, DocProxy, DocStatuses, FilterParams, FilterParamsModel, PermissionEvaluator, Subject, ViewAccess
} from '../common/models';
import { DocView } from './DocView';
import { CommonDocLoader, CommonState } from '../services';
import { ServiceRegistry } from '../services';

interface DocLoaderProps {
  docId: string;
  owningUnitId: string;
  subjectType: string;
  subjectUserId: string;
  onLoading: (loading: boolean) => void;
  onSubjectChange: (subject: Subject | undefined) => void;
  history: History;
  location: Location;
}

interface DocLoaderState extends CommonState {
  listFilters?: FilterParams;
  doc?: Doc;
  page?: number;
  total?: number;
  editedDoc?: Doc;
  editing: boolean;
  saving: boolean;
}

const UNFILED = DocStatuses.UNFILED;

export class DocLoader extends React.Component<DocLoaderProps, DocLoaderState> {
  static contextTypes = { services: PropObject };

  constructor(props: DocLoaderProps) {
    super(props);
    const commonState = CommonDocLoader.createState(props.location);
    this.state = {
      ...commonState,
      page: 0,
      total: 0,
      editing: false,
      saving: false
    };
  }

  async componentDidUpdate(prevProps: DocLoaderProps) {
    if (prevProps.location !== this.props.location) {
      return this.loadInitialData();
    }
  }

  async componentDidMount() {
    return this.loadInitialData();
  }

  loadInitialData = async () => {
    const { errorLogger, commonLoader, docService } = this.context.services as ServiceRegistry;

    this.props.onLoading(true);

    try {
      const { docId } = FilterParamsModel.create(this.props);

      if (!docId) {
        throw new Error('Missing doc id');
      }

      const doc = await docService.findOne(docId);
      const total = docService.cachedIds && docService.cachedIds.total;
      const page = docService.docIdToPage(docId);
      const listFilters = docService.filtersUsedToBuildCache;

      const initialState = await commonLoader.loadInitialState(this.props, this.props.location, doc);

      this.setState(
        {
          ...initialState,
          listFilters,
          doc,
          page,
          total,
          editing: false,
          saving: false
        },
        () => {
          if (initialState.canEdit && doc.status === UNFILED) {
            this.edit();
          }
          this.props.onSubjectChange(this.state.doc && this.state.doc.subject);
        }
      );

    } catch (err) {
      await errorLogger.log(err);
    }

    this.props.onLoading(false);
  };

  edit = () => {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      const editedDoc = DocModel.create(this.state.doc as Doc);
      this.setState({ editing: true, editedDoc });
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  delete = async () => {
    const { docService, errorLogger } = this.context.services as ServiceRegistry;

    try {
      this.setState({ saving: true });
      const doc = await docService.delete(this.state.doc as Doc);
      this.setState({ editing: false, doc, editedDoc: DocModel.create(doc) });
    } catch (err) {
      await errorLogger.log(err);
    }

    this.setState({ saving: false });
  };

  restore = async () => {
    const { docService, errorLogger } = this.context.services as ServiceRegistry;

    try {
      this.setState({ saving: true });
      const doc = await docService.restore(this.state.doc as Doc);
      this.setState({ editing: false, doc, editedDoc: DocModel.create(doc) });
    } catch (err) {
      await errorLogger.log(err);
    }

    this.setState({ saving: false });
  };

  cancel = () => {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      const editedDoc = DocModel.create(this.state.doc as Doc);
      this.setState({ editing: false, editedDoc }, () => errorLogger.clearErrors());
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  save = async () => {
    const { docService, errorLogger } = this.context.services as ServiceRegistry;
    const { doc: prevDoc, page = 1, total = 1 } = this.state;

    try {
      this.setState({ saving: true });
      const doc = await docService.save(this.state.editedDoc as Doc);
      this.setState(
        { editing: false, doc, editedDoc: DocModel.create(doc) },
        () => {
          if (prevDoc && prevDoc.status === DocStatuses.UNFILED && page < total) {
            this.changePage(page + 1);
          }
        }
      );
    } catch (err) {
      await errorLogger.log(err);
    }

    this.setState({ saving: false });
  };

  changeSubject = (subject?: Subject) => {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      const doc = this.state.doc as Doc;
      let owningUnit = doc.owningUnit;
      const docProxy = { subject, owningUnit: doc.owningUnit, viewAccess: doc.viewAccess } as DocProxy;
      const permissionEvaluator = PermissionEvaluator.forUser(this.state.currentUser as User)
        .withPermissions(this.state.permissions)
        .forDoc(docProxy);

      if (subject) {
        if (!permissionEvaluator.canEdit()) {
          const name = subject.user.displayName;
          const type = humanize(subject.type).toLowerCase();
          throw new ValidationError(`You are not authorized to assign ${type} documents to ${name}`);
        }

        // Start with all your overlapping units that can edit this doc
        const unitsAllowedEdit = permissionEvaluator
          .findOwningUnitsOverlap()
          .filter(unit =>
            permissionEvaluator
              .forDoc({ ...docProxy, owningUnit: unit })
              .canEdit()
          );

        // If the doc.owningUnit cannot edit this doc, but the user has another unit that can, default to it
        if (!unitsAllowedEdit.some(unit => unit.id === owningUnit.id) && unitsAllowedEdit.length) {
          owningUnit = unitsAllowedEdit[0];
        }
      }

      const editedDoc = DocModel.create({ ...this.state.editedDoc, subject, owningUnit });
      this.setState(
        { editedDoc },
        () => {
          this.props.onSubjectChange(this.state.editedDoc && this.state.editedDoc.subject);
        }
      );
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  changeOwningUnit = (owningUnit: Organization) => {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      const editedDoc = DocModel.create({ ...this.state.editedDoc, owningUnit });
      this.setState({ editedDoc });
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  changeTitle = (title: string) => {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      const editedDoc = DocModel.create({ ...this.state.editedDoc, title });
      this.setState({ editedDoc });
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  changeViewAccess = (viewAccess: ViewAccess) => {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      const editedDoc = DocModel.create({ ...this.state.editedDoc, viewAccess });
      this.setState({ editedDoc });
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  changeNotes = (details: string) => {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      const editedDoc = DocModel.create({ ...this.state.editedDoc, details });
      this.setState({ editedDoc });
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  changePage = (page: number) => {
    const { docService, errorLogger } = this.context.services as ServiceRegistry;

    try {
      const docId = docService.pageToDocId(page);
      if (!docId) {
        throw new ValidationError('Unable to find the document');
      }
      this.props.history.push(`/doc/${docId}`);
    } catch (err) {
      errorLogger.log(err)
        .catch(logError => console.error(logError));
    }
  };

  render() {
    const { currentUser, page, total, listFilters, editing, saving, permissions, canEdit } = this.state;
    const doc = editing ? this.state.editedDoc : this.state.doc;

    if (!currentUser || !doc) {
      return null;
    }

    return (
      <DocView
        currentUser={currentUser}
        permissions={permissions}
        filters={listFilters}
        doc={doc}
        page={page}
        total={total}
        editing={editing}
        saving={saving}
        canEdit={canEdit}
        onSelectSubject={this.changeSubject}
        onChangeOwningUnit={this.changeOwningUnit}
        onSelectTitle={this.changeTitle}
        onChangeViewAccess={this.changeViewAccess}
        onChangeNotes={this.changeNotes}
        onSave={this.save}
        onCancel={this.cancel}
        onDelete={this.delete}
        onRestore={this.restore}
        onEdit={this.edit}
        onChangePage={this.changePage}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/DocView/DocLoader.tsx